import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import MatterInspectorDrawer from "../components/matters/MatterInspectorDrawer";

// ── Types ────────────────────────────────────────────────────────────

interface MatterInspectorContextValue {
  /** Whether the inspector drawer is currently open */
  isOpen: boolean;
  /** The ID of the matter currently being inspected */
  activeMatterId: string | null;
  /** Open the inspector for a specific matter */
  openMatterInspector: (matterId: string) => void;
  /** Close the inspector (with 300ms delay for exit animation) */
  closeMatterInspector: () => void;
}

const MatterInspectorContext = createContext<MatterInspectorContextValue | null>(null);

export const useMatterInspector = () => {
  const ctx = useContext(MatterInspectorContext);
  if (!ctx) throw new Error("useMatterInspector must be used within MatterInspectorProvider");
  return ctx;
};

// ── Provider ─────────────────────────────────────────────────────────

export const MatterInspectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMatterId, setActiveMatterId] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMatterInspector = useCallback((matterId: string) => {
    // Cancel any pending close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setActiveMatterId(matterId);
    setIsOpen(true);
  }, []);

  const closeMatterInspector = useCallback(() => {
    setIsOpen(false);
    // Delay clearing the matterId so the exit animation can complete
    closeTimerRef.current = setTimeout(() => {
      setActiveMatterId(null);
      closeTimerRef.current = null;
    }, 300);
  }, []);

  return (
    <MatterInspectorContext.Provider
      value={{
        isOpen,
        activeMatterId,
        openMatterInspector,
        closeMatterInspector,
      }}
    >
      {children}
      <MatterInspectorDrawer />
    </MatterInspectorContext.Provider>
  );
};
